import { useCallback, useEffect, useRef, useState } from 'react';

import { useGetProjectNoteQuery, useSaveProjectNoteMutation } from '../api/projectApi';

const AUTOSAVE_DELAY = 800;

export type NoteSaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

export const noteErrorMessage = (error: unknown): string => {
    const err = (error ?? {}) as { status?: unknown; data?: { error?: unknown } };
    switch (err.status) {
        case 400:
            return typeof err.data?.error === 'string' ? err.data.error : 'Некорректный запрос';
        case 401:
            return 'Сессия истекла — войдите заново';
        case 403:
            return 'Проект доступен только его автору';
        case 404:
            return 'Проект не найден';
        case 'FETCH_ERROR':
        case 'TIMEOUT_ERROR':
            return 'Нет связи с сервером';
        default:
            return typeof err.status === 'number' ? `Ошибка сервера (${err.status})` : 'Не удалось сохранить';
    }
};

/**
 * Заметка проекта: GET при открытии проекта + автосохранение с debounce.
 * Текст всегда отправляется целиком (PUT заменяет заметку).
 */
export const useProjectNote = (projectId: string) => {
    const query = useGetProjectNoteQuery(projectId, { refetchOnMountOrArgChange: true });
    const [saveNote] = useSaveProjectNoteMutation();

    const [text, setTextState] = useState('');
    const [saveState, setSaveState] = useState<NoteSaveState>('idle');
    const [saveError, setSaveError] = useState<string | null>(null);

    const textRef = useRef('');
    const savedTextRef = useRef('');
    const initializedForRef = useRef<string | null>(null);
    const timerRef = useRef<number | null>(null);
    const inFlightRef = useRef<Promise<void> | null>(null);
    const projectRef = useRef(projectId);
    projectRef.current = projectId;

    const clearTimer = () => {
        if (timerRef.current !== null) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    // Заполняем поле один раз на проект, когда пришёл свежий ответ GET
    const loaded = query.isSuccess && !query.isFetching;
    useEffect(() => {
        if (!loaded || initializedForRef.current === projectId) return;
        const value = query.data?.text ?? '';
        initializedForRef.current = projectId;
        textRef.current = value;
        savedTextRef.current = value;
        setTextState(value);
        setSaveState('idle');
        setSaveError(null);
    }, [loaded, projectId, query.data]);

    const flush = useCallback(
        async (options: { keepalive?: boolean } = {}): Promise<void> => {
            clearTimer();
            const pid = projectRef.current;
            // PUT-ы не должны обгонять друг друга
            if (inFlightRef.current) await inFlightRef.current;
            if (pid !== projectRef.current || initializedForRef.current !== pid) return;

            const value = textRef.current;
            if (value === savedTextRef.current) {
                setSaveState((state) => (state === 'dirty' || state === 'error' ? 'saved' : state));
                return;
            }

            setSaveState('saving');
            setSaveError(null);

            const run = (async () => {
                try {
                    await saveNote({ projectId: pid, text: value, ...options }).unwrap();
                    if (pid !== projectRef.current) return;
                    savedTextRef.current = value;
                    setSaveState(textRef.current === value ? 'saved' : 'dirty');
                } catch (error) {
                    if (pid !== projectRef.current) return;
                    setSaveError(noteErrorMessage(error));
                    setSaveState('error');
                }
            })();

            inFlightRef.current = run;
            await run;
            inFlightRef.current = null;

            // доотправляем то, что набрали во время запроса
            if (pid === projectRef.current && textRef.current !== savedTextRef.current && !timerRef.current) {
                timerRef.current = window.setTimeout(() => void flush(), AUTOSAVE_DELAY);
            }
        },
        [saveNote],
    );

    const setText = useCallback(
        (value: string) => {
            textRef.current = value;
            setTextState(value);
            setSaveState(value === savedTextRef.current ? 'saved' : 'dirty');
            clearTimer();
            timerRef.current = window.setTimeout(() => void flush(), AUTOSAVE_DELAY);
        },
        [flush],
    );

    // Уход со страницы / смена проекта — отправляем несохранённое
    useEffect(() => {
        const onBeforeUnload = () => {
            if (textRef.current !== savedTextRef.current) void flush({ keepalive: true });
        };
        window.addEventListener('beforeunload', onBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            clearTimer();
            // фиксируем синхронно: дальше refs будут уже про другой проект
            const value = textRef.current;
            if (initializedForRef.current === projectId && value !== savedTextRef.current) {
                savedTextRef.current = value;
                void saveNote({ projectId, text: value, keepalive: true });
            }
        };
    }, [projectId, flush, saveNote]);

    const loading = initializedForRef.current !== projectId && !query.isError;

    return {
        text,
        setText,
        note: query.data ?? null,
        hasNote: query.data != null,
        loading,
        loadError: query.isError ? noteErrorMessage(query.error) : null,
        reload: () => void query.refetch(),
        saveState,
        saveError,
        flush,
    };
};

export type ProjectNoteController = ReturnType<typeof useProjectNote>;